import {
  ExpressionSettingsLineType,
  IChartSettings,
  IDisplayedHighlight,
  IHighlightableChartData,
  IMeasurableComparableSliceColumn,
  IMeasurableSliceColumn,
  IParsedChartData,
  IParsedChartDataExpression,
  IParsedChartDataExpressionSlice,
  IParsedChartDataFunnelSlice,
  IParsedExpressionColumn,
  IParsedFunnelColumn,
} from '../interfaces';
import { CHART_TYPES } from '../constants/chartTypes';
import { IBreakdownOverallStatus } from '../../../utils/interfaces';
import { isNil } from 'lodash';
import { FunnelModeKey, QueryType } from '../../../utils/apollo/graphqlSchema';
import { assertCondition } from '../../../utils/types';
import getFunnelVisibleLines from './funnel/visibleLines/getFunnelVisibleLines';
import {
  getMeasureColorFromColorConfig,
  getSliceColorFromColorConfig,
  IAnalysisColorConfig,
  isShouldUseMeasureColor,
} from '../utils/analysisColorConfig';
import { UnreachableCaseError } from 'ts-essentials';

export const IGNORED_LINE_NAME = '__ignored' as const;

export type IChartLineType = 'normal' | 'dashed' | 'pattern';

export interface IChartLine {
  name: string | typeof IGNORED_LINE_NAME;
  display: string;
  color: string;
  type: IChartLineType;
  measureKey: string;
}

interface IProps {
  chartSettings: IChartSettings;
  chartData: IParsedChartData;
  isBreakdownOverall: IBreakdownOverallStatus;
  isHistogram?: boolean;
  colorConfig: IAnalysisColorConfig;
}

/**
 * From all highlights, return those which are on the visible lines
 *
 * @param visibleLines - currently displayed lines
 * @param chartData - Expression/Funnel data containing highlights
 */
const generateDisplayedHighlights = (
  visibleLines: IChartLine[],
  chartData: IHighlightableChartData,
): IDisplayedHighlight[] => {
  const { highlights } = chartData;
  return highlights.reduce<IDisplayedHighlight[]>((result, highlight) => {
    const relatedLine = visibleLines.find(
      (vl) => vl.name === highlight.relatedLineName,
    );
    if (relatedLine) {
      result.push({ ...highlight, relatedLine });
    }
    return result;
  }, []);
};

/**
 * in Expression, logic rely on 2 slice fields: resultMeasureColumnIndex and variables.
 * resultMeasureColumnIndex is optional and can refer to result measure;
 * variables are referring to measure 1, 2, 3, etc.
 */
const parseExpressionSliceSetting = (
  columns: IParsedExpressionColumn[],
  slice: IParsedChartDataExpressionSlice,
): IParsedExpressionColumn[] => {
  const { variables, resultMeasureColumnIndex } = slice;
  const result: IParsedExpressionColumn[] = [];

  if (variables.length > 0) {
    variables.forEach((columnIndex) => {
      result.push(columns[columnIndex]);
    });
  }

  if (!isNil(resultMeasureColumnIndex)) {
    const resultColumn = columns[resultMeasureColumnIndex];

    if (resultColumn) {
      result.unshift(resultColumn);
    }
  }

  return result;
};

/**
 * Reorder columns by the order of slices (if any) by keeping related columns
 * next to each other. If there are no slices, use order of measures
 */
const reorderExpressionColumns = (
  columns: IParsedExpressionColumn[],
  slices: IParsedChartDataExpressionSlice[],
  chartSettings: IChartSettings,
): IParsedExpressionColumn[] => {
  const {
    displayedSliceSettings,
    displayedMeasureSettings,
    expressionSettings,
  } = chartSettings;
  const result: IParsedExpressionColumn[] = [];

  // Reordering columns by the sequence of displayedSlices and displayedMeasure
  if (displayedSliceSettings.length > 0) {
    // follow the sequence of displayedSliceSettings
    for (const sliceSetting of displayedSliceSettings) {
      const currSlice = slices.find((slice) => slice.key === sliceSetting.key);

      /**
       * in Expression, logic rely on 2 slice fields: resultMeasureColumnIndex and variables.
       * resultMeasureColumnIndex is optional and can refer to result measure;
       * variables are referring to measure 1, 2, 3, etc.
       */
      if (currSlice) {
        result.push(...parseExpressionSliceSetting(columns, currSlice));

        // KBT-1014 if showCompare chart setting is on,
        // if exists - add columns for comparison slice
        if (expressionSettings && expressionSettings?.showCompare) {
          const comparisonSlice = slices.find(
            (slice) =>
              slice.isComparison && slice.originalSliceKey === currSlice.key,
          );

          if (comparisonSlice) {
            result.push(
              ...parseExpressionSliceSetting(columns, comparisonSlice),
            );
          }
        }
      }
    }
  } else {
    for (const measure of displayedMeasureSettings) {
      const column = columns.find((col) => col.measureKey === measure.key);
      if (isNil(column)) {
        continue;
      }
      result.push(column);
    }
  }

  return result;
};

const reorderFunnelColumns = (
  columns: IParsedFunnelColumn[],
  slices: IParsedChartDataFunnelSlice[],
  chartSettings: IChartSettings,
): IParsedFunnelColumn[] => {
  const { displayedSliceSettings, displayedMeasureSettings } = chartSettings;
  const result: IParsedFunnelColumn[] = [];
  // Reordering columns by the sequence of displayedSlices and displayedMeasure
  if (displayedSliceSettings.length > 0) {
    // follow the sequence of displayedSliceSettings
    for (const sliceSetting of displayedSliceSettings) {
      const currSlice = slices.find((slice) => slice.key === sliceSetting.key);

      /**
       * in Funnel, logic rely on 2 slice fields: measure and variables.
       * measure is the first step, variables is step 1, 2, 3, etc
       */
      if (currSlice) {
        const { measure, variables } = currSlice;
        const sliceGroup: IParsedFunnelColumn[] = [];

        if (variables.length > 0) {
          variables.forEach((columnIndex) => {
            sliceGroup.push(columns[columnIndex]);
          });
        }

        const resultMeasure = columns[measure];

        if (resultMeasure) {
          sliceGroup.unshift(resultMeasure);
        }

        result.push(...sliceGroup);
      }
    }
  } else {
    for (const measure of displayedMeasureSettings) {
      const column = columns.find((col) => col.measureKey === measure.key);
      if (isNil(column)) {
        continue;
      }
      result.push(column);
    }
  }
  return result;
};

/**
 * Return columns only if they are visible based on the slices and measures selection from chart settings
 */
const filterVisibleColumnsByMeasure = <T extends IMeasurableSliceColumn>(
  columns: T[],
  chartSettings: IChartSettings,
) => {
  const { displayedMeasureSettings } = chartSettings;

  const visibleMeasureKeys = displayedMeasureSettings.reduce<string[]>(
    (acc, measure) => {
      if (measure.isVisible) {
        acc.push(measure.key);
      }

      return acc;
    },
    [],
  );

  return columns.filter((column) =>
    visibleMeasureKeys.includes(column.measureKey),
  );
};

export const filterVisibleColumnsBySlice = <T extends IMeasurableSliceColumn>(
  columns: T[],
  chartSettings: IChartSettings,
): T[] => {
  const { displayedSliceSettings } = chartSettings;

  return columns.filter((column) => {
    const foundSetting = displayedSliceSettings.find(
      (setting) => setting.key === (column.originalSliceKey || column.sliceKey),
    );

    return foundSetting && foundSetting.isVisible;
  });
};

export const filterVisibleColumnsBySliceAndMeasure = <
  T extends IMeasurableSliceColumn,
>(
  columns: T[],
  chartSettings: IChartSettings,
) => {
  const visibleColumnsByMeasure = filterVisibleColumnsByMeasure(
    columns,
    chartSettings,
  );
  return filterVisibleColumnsBySlice(visibleColumnsByMeasure, chartSettings);
};

/**
 * Generate chart lines for Expression query.
 * Takes into account the selected lineType from expressionSettings
 */
const expressionChartLines = (
  visibleColumns: IMeasurableComparableSliceColumn[],
  chartSettings: IChartSettings,
  chartData: IParsedChartDataExpression,
  colorConfig: IAnalysisColorConfig,
  isBreakdownOverall: IBreakdownOverallStatus,
): IChartLine[] => {
  const { linesMeta } = chartData;
  const { chartType, expressionSettings } = chartSettings;
  const shouldUseMeasureColor = isShouldUseMeasureColor(isBreakdownOverall);
  const visibleLines: IChartLine[] = [];

  // create lines by visible columns, following selected line type
  // append measure line to the return array
  for (const column of visibleColumns) {
    const lineMeta = linesMeta[column.key];
    const lineType = expressionSettings?.lineType;

    const color = shouldUseMeasureColor
      ? getMeasureColorFromColorConfig(colorConfig, lineMeta.measureKey)
      : getSliceColorFromColorConfig(
          colorConfig,
          column?.originalSliceKey ?? column.sliceKey,
        );

    // KBT-946 prepare visible lines for Area and StackedColumn charts only
    if (
      chartType === CHART_TYPES.PERCENT_STACKED_AREA_CHART ||
      chartType === CHART_TYPES.STACKED_COLUMN_CHART
    ) {
      visibleLines.push(
        {
          name: lineMeta.percentLineKey ?? '',
          display: lineMeta.percentLineName ?? '',
          color,
          type: 'normal',
          measureKey: column.measureKey,
        },
        {
          name: column.key,
          display: lineMeta.display,
          color,
          type: 'normal',
          measureKey: column.measureKey,
        },
      );
    } else {
      if (expressionSettings?.showMeasure && !column.isComparison) {
        visibleLines.push({
          name: column.key,
          display: lineMeta.display,
          color,
          type: 'normal',
          measureKey: column.measureKey,
        });
      }

      if (expressionSettings?.showCompare && column.isComparison) {
        visibleLines.push({
          name: column.key,
          display: lineMeta.display,
          color,
          type: 'pattern',
          measureKey: column.measureKey,
        });
      }

      if (lineType === ExpressionSettingsLineType.Ma) {
        visibleLines.push({
          name: lineMeta.maLineKey ?? '',
          display: lineMeta.maLineName ?? '',
          color,
          type: 'dashed',
          measureKey: column.measureKey,
        });
      }

      if (lineType === ExpressionSettingsLineType.Ma30) {
        visibleLines.push({
          name: lineMeta.ma30LineKey ?? '',
          display: lineMeta.ma30LineName ?? '',
          color,
          type: 'dashed',
          measureKey: column.measureKey,
        });
      }

      if (lineType === ExpressionSettingsLineType.Ms7) {
        visibleLines.push({
          name: lineMeta.ms7LineKey ?? '',
          display: lineMeta.ms7LineName ?? '',
          color,
          type: 'dashed',
          measureKey: column.measureKey,
        });
      }

      if (lineType === ExpressionSettingsLineType.Ms30) {
        visibleLines.push({
          name: lineMeta.ms30LineKey ?? '',
          display: lineMeta.ms30LineName ?? '',
          color,
          type: 'dashed',
          measureKey: column.measureKey,
        });
      }

      if (lineType === ExpressionSettingsLineType.CumulativeSum) {
        visibleLines.push({
          name: lineMeta.cumulativeLineKey ?? '',
          display: lineMeta.cumulativeLineName ?? '',
          color,
          type: 'dashed',
          measureKey: column.measureKey,
        });
      }
    }
  }
  return visibleLines;
};

interface IReturn {
  visibleLines: IChartLine[];
  displayedHighlights: IDisplayedHighlight[];
}

export default ({
  chartSettings,
  chartData,
  isBreakdownOverall,
  colorConfig,
}: IProps): IReturn => {
  // get all visible measures and groups
  switch (chartData.queryType) {
    case QueryType.Expression: {
      const reorderedColumns = reorderExpressionColumns(
        chartData.columns,
        chartData.slices,
        chartSettings,
      );
      const visibleColumns = filterVisibleColumnsBySliceAndMeasure(
        reorderedColumns,
        chartSettings,
      );
      const visibleLines = expressionChartLines(
        visibleColumns,
        chartSettings,
        chartData,
        colorConfig,
        isBreakdownOverall,
      );
      const displayedHighlights = generateDisplayedHighlights(
        visibleLines,
        chartData,
      );

      return {
        visibleLines,
        displayedHighlights,
      };
    }
    case QueryType.Funnel: {
      const reorderedColumns = reorderFunnelColumns(
        chartData.columns,
        chartData.slices,
        chartSettings,
      );
      const visibleLines = getFunnelVisibleLines(
        reorderedColumns,
        chartData,
        chartSettings,
        isBreakdownOverall,
        colorConfig,
      );

      const displayedHighlights =
        chartData.mode === FunnelModeKey.Conversion
          ? generateDisplayedHighlights(visibleLines, chartData)
          : [];

      return {
        visibleLines,
        displayedHighlights,
      };
    }
    case QueryType.Retention:
      assertCondition(
        false,
        'Retention chart lines should be built with useRetentionLines hook',
      );
      break;
    case QueryType.Path:
    case QueryType.DataTable:
    case QueryType.Prediction:
    case QueryType.CohortSample:
    case QueryType.Significance:
      assertCondition(
        false,
        `Chart lines are not applicable to ${chartData.queryType}`,
      );
      break;
    default:
      throw new UnreachableCaseError(chartData);
  }
};
